import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SolutionRankingPanel = ({ selectedError, errors }) => {
  const [solutions, setSolutions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedSolution, setExpandedSolution] = useState(null);

  useEffect(() => {
    if (selectedError) {
      generateSolutions(selectedError);
    }
  }, [selectedError]);

  const generateSolutions = (error) => {
    setLoading(true);

    // Mock AI-generated solutions (in production, this would call Anthropic API)
    const generatedSolutions = [
      {
        id: 1,
        title: 'Implement Circuit Breaker Pattern',
        description: 'Add circuit breaker to prevent cascading failures when external service is unavailable',
        complexity: 'medium',
        successProbability: 95,
        businessImpact: 'high',
        implementationTime: '2-4 hours',
        steps: [
          'Install circuit breaker library (e.g., opossum)',
          'Wrap external API calls with circuit breaker',
          'Configure failure threshold and timeout',
          'Add fallback mechanism for open circuit state',
          'Implement monitoring and alerting for circuit state changes'
        ],
        codeExample: `const CircuitBreaker = require('opossum');\n\nconst options = {\n  timeout: 3000,\n  errorThresholdPercentage: 50,\n  resetTimeout: 30000\n};\n\nconst breaker = new CircuitBreaker(apiCall, options);`,
        estimatedROI: 'Prevents 90% of cascading failures'
      },
      {
        id: 2,
        title: 'Add Request Retry Logic with Exponential Backoff',
        description: 'Implement intelligent retry mechanism to handle transient failures gracefully',
        complexity: 'low',
        successProbability: 88,
        businessImpact: 'medium',
        implementationTime: '1-2 hours',
        steps: [
          'Install retry library (e.g., axios-retry)',
          'Configure retry count and delay strategy',
          'Implement exponential backoff algorithm',
          'Add jitter to prevent thundering herd',
          'Log retry attempts for monitoring'
        ],
        codeExample: `import axiosRetry from 'axios-retry'
;\n\naxiosRetry(axios, {\n  retries: 3,\n  retryDelay: axiosRetry.exponentialDelay,\n  retryCondition: (error) => {\n    return error.response?.status >= 500;\n  }\n});`,
        estimatedROI: 'Resolves 75% of transient errors automatically'
      },
      {
        id: 3,
        title: 'Enhance Error Logging and Monitoring',
        description: 'Improve observability with structured logging and real-time alerting',
        complexity: 'low',
        successProbability: 92,
        businessImpact: 'medium',
        implementationTime: '1-3 hours',
        steps: [
          'Implement structured logging with context',
          'Add correlation IDs for request tracing',
          'Configure log aggregation (e.g., ELK stack)',
          'Set up real-time alerting for critical errors',
          'Create dashboards for error trends'
        ],
        codeExample: `const logger = winston.createLogger({\n  format: winston.format.combine(\n    winston.format.timestamp(),\n    winston.format.json()\n  ),\n  transports: [\n    new winston.transports.Console(),\n    new winston.transports.File({ filename: 'error.log' })\n  ]\n});`,
        estimatedROI: 'Reduces mean time to detection by 60%'
      },
      {
        id: 4,
        title: 'Implement Rate Limiting and Throttling',
        description: 'Protect system from overload by controlling request rate',
        complexity: 'medium',
        successProbability: 85,
        businessImpact: 'high',
        implementationTime: '3-5 hours',
        steps: [
          'Choose rate limiting strategy (token bucket, leaky bucket)',
          'Implement rate limiter middleware',
          'Configure limits per user/IP/endpoint',
          'Add rate limit headers to responses',
          'Implement graceful degradation for exceeded limits'
        ],
        codeExample: `const rateLimit = require('express-rate-limit');\n\nconst limiter = rateLimit({\n  windowMs: 15 * 60 * 1000,\n  max: 100,\n  message: 'Too many requests'\n});\n\napp.use('/api/', limiter);`,
        estimatedROI: 'Prevents 80% of overload-related failures'
      }
    ];

    // Sort by priority score (combination of success probability and business impact)
    const sortedSolutions = generatedSolutions?.sort((a, b) => {
      const scoreA = a?.successProbability * (a?.businessImpact === 'high' ? 1.5 : a?.businessImpact === 'medium' ? 1.2 : 1);
      const scoreB = b?.successProbability * (b?.businessImpact === 'high' ? 1.5 : b?.businessImpact === 'medium' ? 1.2 : 1);
      return scoreB - scoreA;
    });

    setSolutions(sortedSolutions);
    setLoading(false);
  };

  const getComplexityColor = (complexity) => {
    const colors = {
      low: 'bg-green-100 text-green-700 border-green-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      high: 'bg-red-100 text-red-700 border-red-200'
    };
    return colors?.[complexity] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const getImpactColor = (impact) => {
    const colors = {
      high: 'text-red-600',
      medium: 'text-orange-600',
      low: 'text-blue-600'
    };
    return colors?.[impact] || 'text-gray-600';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900">Prioritized Solution Recommendations</h3>
        <p className="text-sm text-gray-600 mt-1">AI-ranked solutions by implementation complexity, success probability, and business impact</p>
      </div>

      {!selectedError ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <Icon name="Target" className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No error selected</p>
          <p className="text-sm text-gray-500 mt-1">Select an error from the sidebar to view solution recommendations</p>
        </div>
      ) : loading ? (
        <div className="flex items-center justify-center py-12">
          <Icon name="Loader" className="w-8 h-8 text-purple-600 animate-spin" />
        </div>
      ) : (
        <div className="space-y-4">
          {solutions?.map((solution, index) => (
            <div
              key={solution?.id}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                      #{index + 1}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900 mb-1">{solution?.title}</h4>
                      <p className="text-sm text-gray-600">{solution?.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setExpandedSolution(expandedSolution === solution?.id ? null : solution?.id)}
                    className="ml-3 text-gray-400 hover:text-gray-600"
                  >
                    <Icon name={expandedSolution === solution?.id ? 'ChevronUp' : 'ChevronDown'} className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-4 gap-3 mb-3">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Complexity</p>
                    <span className={`text-xs font-medium px-2 py-1 rounded border ${getComplexityColor(solution?.complexity)}`}>
                      {solution?.complexity?.toUpperCase()}
                    </span>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Success Rate</p>
                    <p className="text-sm font-bold text-green-600">{solution?.successProbability}%</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Impact</p>
                    <p className={`text-sm font-bold ${getImpactColor(solution?.businessImpact)}`}>
                      {solution?.businessImpact?.toUpperCase()}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Time</p>
                    <p className="text-sm font-medium text-gray-700">{solution?.implementationTime}</p>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-sm text-green-800">
                    <Icon name="TrendingUp" className="w-4 h-4" />
                    <span className="font-medium">Estimated ROI:</span>
                    <span>{solution?.estimatedROI}</span>
                  </div>
                </div>
              </div>

              {expandedSolution === solution?.id && (
                <div className="border-t border-gray-200 bg-gray-50 p-5 space-y-4">
                  <div>
                    <h5 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Icon name="List" className="w-4 h-4 text-purple-600" />
                      Implementation Steps
                    </h5>
                    <ol className="space-y-2">
                      {solution?.steps?.map((step, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <span className="flex-shrink-0 w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                          </span>
                          <span className="text-sm text-gray-700 pt-0.5">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div>
                    <h5 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                      <Icon name="Code" className="w-4 h-4 text-purple-600" />
                      Code Example
                    </h5>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-xs">
                      <code>{solution?.codeExample}</code>
                    </pre>
                  </div>

                  <div className="flex gap-3">
                    <Button className="flex-1 bg-purple-600 hover:bg-purple-700">
                      <Icon name="Play" className="w-4 h-4 mr-2" />
                      Start Implementation
                    </Button>
                    <Button variant="outline" className="flex-1">
                      <Icon name="BookOpen" className="w-4 h-4 mr-2" />
                      View Documentation
                    </Button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SolutionRankingPanel;